import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, Award, ShieldCheck, BookOpen, Users, Rocket,
  Brain, Palette, Video, Globe2, Code2, Mic2, Wand2, Bot,
  GraduationCap, Trophy, Star, Heart, ChevronDown, Calendar,
  PlayCircle, MessageCircle, CheckCircle2, AlertCircle, Lock,
  Lightbulb, Camera, FileText, Zap
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const YT_VIDEO_ID = '38twj2cLyjo';
const YT_EMBED_URL = `https://www.youtube.com/embed/${YT_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
const YT_THUMB_URL = `https://i.ytimg.com/vi/${YT_VIDEO_ID}/hqdefault.jpg`;
const WA_ENROL = 'https://wa.me/19832080995?text=Hi!%20I%27m%20interested%20in%20AI%20STEM%20Kidz';
const WA_PLAIN = 'https://wa.me/19832080995';
const CALENDLY = 'https://calendly.com/dmt-a-master';
const HANDBOOK_URL = 'https://a.co/d/0ccRhiVz';

// Unsplash imagery (verified URLs of real people matching each section's intent)
const IMG_KID_CODING = 'https://images.unsplash.com/photo-1597933471507-1ca5765185d8?auto=format&fit=crop&w=900&q=80';
const IMG_PARENT_CHILD = 'https://plus.unsplash.com/premium_photo-1723773617596-830520043de4?auto=format&fit=crop&w=900&q=80';
const IMG_CERTIFICATE = 'https://plus.unsplash.com/premium_photo-1774050778327-c66bcf1b835f?auto=format&fit=crop&w=900&q=80';

// ---------- Section: Hero ----------
function Hero({ t, spots }) {
  const [playing, setPlaying] = useState(false);

  return (
    <section className="relative pt-32 pb-20 overflow-hidden" data-testid="kidz-hero">
      <div className="hero-glow" />
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,215,0,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(124,58,237,0.25), transparent 45%)' }} />
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-[var(--accent-gold)] mb-6">
              <Sparkles className="w-4 h-4 text-[var(--accent-gold)]" />
              <span className="text-xs uppercase tracking-wider font-semibold text-[var(--accent-gold)]">
                {t('aiStemKidz.hero.tag')}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-[var(--text-primary)]">{t('aiStemKidz.hero.headlinePart1')} </span>
              <span className="gradient-text-gold">{t('aiStemKidz.hero.headlinePart2')}</span>
            </h1>
            <p className="text-base md:text-lg text-[var(--text-secondary)] mb-8 max-w-xl">
              {t('aiStemKidz.hero.subhead')}
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <a href={WA_ENROL} target="_blank" rel="noopener noreferrer"
                 data-testid="hero-enrol-btn"
                 className="btn-primary inline-flex items-center gap-2 pulse-cta">
                {t('aiStemKidz.hero.cta')} <Rocket className="w-4 h-4" />
              </a>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-[var(--border-subtle)]">
                <ShieldCheck className="w-4 h-4 text-[var(--primary-light)]" />
                <span className="text-xs text-[var(--text-secondary)]">{t('aiStemKidz.hero.trust')}</span>
              </div>
            </div>
            <div className="p-4 rounded-2xl glass border border-[var(--border-highlight)]" data-testid="hero-spots">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  <div className="absolute inset-0 w-3 h-3 rounded-full bg-red-500 animate-ping opacity-75" />
                </div>
                <span className="text-sm text-[var(--text-primary)]">
                  <span className="font-bold text-[var(--accent-gold)]">{spots}</span> {t('aiStemKidz.hero.spotsLeft')}
                </span>
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-2">{t('aiStemKidz.hero.foundingNote')}</p>
            </div>
          </motion.div>

          {/* YouTube Short — click-to-load thumbnail (no autoload for performance) */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                      className="flex justify-center">
            <div className="relative w-full max-w-[340px] sm:max-w-[380px] aspect-[9/16] rounded-3xl overflow-hidden glass-strong border border-[var(--border-highlight)] group"
                 data-testid="hero-video-container">
              {playing ? (
                <iframe
                  src={YT_EMBED_URL}
                  title="AI STEM Kidz intro"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="hero-video-iframe"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  aria-label={t('aiStemKidz.hero.playLabel')}
                  data-testid="hero-video-play"
                  className="absolute inset-0 w-full h-full cursor-pointer"
                >
                  <img
                    src={YT_THUMB_URL}
                    alt="AI STEM Kidz intro video thumbnail"
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/25 transition-all">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-20 h-20 rounded-full bg-[var(--accent-gold)] flex items-center justify-center glow-gold group-hover:scale-110 transition-transform">
                        <PlayCircle className="w-12 h-12 text-[var(--bg-default)]" />
                      </div>
                      <span className="px-4 py-1 rounded-full bg-black/60 text-xs text-white">
                        {t('aiStemKidz.hero.playLabel')}
                      </span>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </motion.div>
        </div>
        <p className="text-xs text-center text-[var(--text-muted)] mt-4 lg:hidden">
          {t('aiStemKidz.hero.videoCaption')}
        </p>
      </div>
    </section>
  );
}

// ---------- Section: Problem vs Solution ----------
function ProblemSolution({ t }) {
  const problems = t('aiStemKidz.problem.list');
  const solutions = t('aiStemKidz.solution.list');
  return (
    <section className="py-20 bg-[var(--bg-paper)]" data-testid="kidz-problem-solution">
      <div className="container-custom">
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      className="p-8 rounded-3xl glass border border-red-500/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--text-primary)]">{t('aiStemKidz.problem.title')}</h2>
            </div>
            <ul className="space-y-4">
              {problems.map((item, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`problem-item-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                  <span className="text-sm text-[var(--text-secondary)]">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                      className="p-8 rounded-3xl glass border border-[var(--accent-gold)]/30">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-[var(--accent-gold)]/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[var(--accent-gold)]" />
              </div>
              <h2 className="text-2xl font-bold gradient-text-gold">{t('aiStemKidz.solution.title')}</h2>
            </div>
            <ul className="space-y-4">
              {solutions.map((item, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`solution-item-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-[var(--accent-gold)] mt-1 flex-shrink-0" />
                  <span className="text-sm text-[var(--text-primary)]">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Value Proposition ----------
function ValueProp({ t }) {
  return (
    <section className="py-20" data-testid="kidz-value-prop">
      <div className="container-custom">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                    className="max-w-6xl mx-auto rounded-3xl glass-strong border border-[var(--border-highlight)] overflow-hidden relative">
          <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-[var(--primary-main)] opacity-20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -right-20 w-60 h-60 rounded-full bg-[var(--accent-gold)] opacity-15 blur-3xl pointer-events-none" />
          <div className="grid md:grid-cols-5 gap-0 relative z-10">
            <div className="md:col-span-2 relative min-h-[260px] md:min-h-full">
              <img
                src={IMG_KID_CODING}
                alt="Child creating with AI on a laptop"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="img-kid-coding"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[var(--bg-paper)] md:to-[var(--bg-paper)]/80" />
            </div>
            <div className="md:col-span-3 p-8 md:p-10 text-center md:text-left">
              <Sparkles className="w-10 h-10 text-[var(--accent-gold)] mx-auto md:mx-0 mb-6" />
              <p className="text-lg md:text-xl text-[var(--text-primary)] font-medium leading-relaxed mb-6">
                "{t('aiStemKidz.valueProp.quote')}"
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-6">
                {t('aiStemKidz.valueProp.highlights').map((h, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full glass border border-[var(--accent-gold)]/40 text-xs text-[var(--accent-gold)]">
                    {h}
                  </span>
                ))}
              </div>
              <div className="p-4 rounded-2xl bg-gradient-to-r from-[var(--primary-main)]/10 to-[var(--accent-gold)]/10 border border-[var(--accent-gold)]/30">
                <p className="text-sm text-[var(--text-primary)]">
                  <span className="text-[var(--accent-gold)] font-bold">"La IA y más allá IA"</span> — {t('aiStemKidz.valueProp.spanishNote')}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ---------- Section: How It Works ----------
function HowItWorks({ t }) {
  const steps = [
    { icon: GraduationCap, ...t('aiStemKidz.how.s1') },
    { icon: Rocket, ...t('aiStemKidz.how.s2') },
    { icon: Heart, ...t('aiStemKidz.how.s3') },
    { icon: Trophy, ...t('aiStemKidz.how.s4') },
  ];
  return (
    <section className="py-20 bg-[var(--bg-paper)]" data-testid="kidz-how">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          <span className="gradient-text-gold">{t('aiStemKidz.how.title')}</span>
        </h2>
        <p className="text-center text-[var(--text-secondary)] mb-12 max-w-2xl mx-auto">
          {t('aiStemKidz.how.subtitle')}
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {steps.map((step, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                        className="relative p-6 rounded-2xl glass border border-[var(--border-subtle)] card-hover"
                        data-testid={`how-step-${i + 1}`}>
              <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-[var(--accent-gold)] text-[var(--bg-default)] flex items-center justify-center font-bold">
                {i + 1}
              </div>
              <div className="w-12 h-12 rounded-2xl bg-[var(--primary-main)]/20 flex items-center justify-center mb-4">
                <step.icon className="w-6 h-6 text-[var(--primary-light)]" />
              </div>
              <h3 className="font-bold mb-2 text-[var(--text-primary)]">{step.title}</h3>
              <p className="text-sm text-[var(--text-secondary)]">{step.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center">
          <a href={WA_ENROL} target="_blank" rel="noopener noreferrer"
             data-testid="how-cta-btn" className="btn-primary inline-flex items-center gap-2 pulse-cta">
            {t('aiStemKidz.how.cta')} <Rocket className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Curriculum & Tools ----------
function Curriculum({ t }) {
  const tools = [
    { name: 'Canva AI', icon: Palette },
    { name: 'Leonardo', icon: Wand2 },
    { name: 'ChatGPT (kid-safe)', icon: Bot },
    { name: 'DeepSeek (kid-safe)', icon: Brain },
    { name: 'Nano Banana', icon: Sparkles },
    { name: 'Wan Video', icon: Video },
    { name: 'Runway ML', icon: Camera },
    { name: 'ElevenLabs', icon: Mic2 },
    { name: 'Emergent', icon: Zap },
    { name: 'Code Studio', icon: Code2 },
    { name: 'Storyboard AI', icon: FileText },
    { name: 'Web Builder', icon: Globe2 },
  ];
  const projects = t('aiStemKidz.curriculum.projects');
  return (
    <section className="py-20" data-testid="kidz-curriculum">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          <span className="text-[var(--text-primary)]">{t('aiStemKidz.curriculum.title')} </span>
          <span className="gradient-text-gold">12+</span>
        </h2>
        <p className="text-center text-[var(--text-secondary)] mb-12 max-w-2xl mx-auto">
          {t('aiStemKidz.curriculum.subtitle')}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-16">
          {tools.map((tool, i) => (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                        className="p-4 rounded-2xl glass border border-[var(--border-subtle)] hover:border-[var(--accent-gold)] transition-all text-center group"
                        data-testid={`tool-${i}`}>
              <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-[var(--primary-main)]/20 to-[var(--accent-gold)]/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <tool.icon className="w-6 h-6 text-[var(--primary-light)] group-hover:text-[var(--accent-gold)] transition-colors" />
              </div>
              <span className="text-xs text-[var(--text-secondary)] font-medium">{tool.name}</span>
            </motion.div>
          ))}
        </div>
        <div className="text-center mb-8">
          <h3 className="text-xl font-bold text-[var(--text-primary)] mb-4">{t('aiStemKidz.curriculum.sampleTitle')}</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {projects.map((p, i) => (
            <div key={i} className="p-5 rounded-2xl bg-gradient-to-br from-[var(--primary-main)]/10 to-[var(--accent-gold)]/5 border border-[var(--border-highlight)] text-center"
                 data-testid={`project-${i}`}>
              <Lightbulb className="w-8 h-8 text-[var(--accent-gold)] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-primary)] font-medium">{p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Parental Co-Pilot ----------
function ParentalCoPilot({ t }) {
  const features = t('aiStemKidz.parental.features');
  return (
    <section className="py-20 bg-[var(--bg-paper)]" data-testid="kidz-parental">
      <div className="container-custom">
        <div className="max-w-6xl mx-auto rounded-3xl glass-strong border border-[var(--border-highlight)] overflow-hidden relative">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[var(--primary-main)] opacity-15 blur-3xl pointer-events-none" />
          <div className="grid md:grid-cols-2 gap-0 items-stretch relative z-10">
            <div className="p-8 md:p-12 order-2 md:order-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--accent-gold)]/10 border border-[var(--accent-gold)]/30 mb-4">
                <Heart className="w-4 h-4 text-[var(--accent-gold)]" />
                <span className="text-xs font-semibold text-[var(--accent-gold)] uppercase tracking-wider">
                  {t('aiStemKidz.parental.tag')}
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[var(--text-primary)]">
                {t('aiStemKidz.parental.title')}
              </h2>
              <p className="text-[var(--text-secondary)] mb-6">
                {t('aiStemKidz.parental.subtitle')}
              </p>
              <div className="space-y-4">
                {features.map((f, i) => (
                  <div key={i} className="flex items-start gap-3" data-testid={`parental-feature-${i}`}>
                    <ShieldCheck className="w-5 h-5 text-[var(--accent-gold)] mt-1 flex-shrink-0" />
                    <span className="text-sm text-[var(--text-primary)]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative min-h-[320px] md:min-h-full order-1 md:order-2">
              <img
                src={IMG_PARENT_CHILD}
                alt="Parent and child learning together on a tablet"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="img-parent-child"
              />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[var(--bg-paper)] md:to-[var(--bg-paper)]/70" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Certification & Portfolio ----------
function Certification({ t }) {
  const badges = [
    { label: t('aiStemKidz.cert.b1'), color: 'from-yellow-400 to-amber-500', icon: Sparkles },
    { label: t('aiStemKidz.cert.b2'), color: 'from-purple-400 to-indigo-500', icon: Wand2 },
    { label: t('aiStemKidz.cert.b3'), color: 'from-green-400 to-emerald-500', icon: ShieldCheck },
    { label: t('aiStemKidz.cert.b4'), color: 'from-pink-400 to-rose-500', icon: Palette },
    { label: t('aiStemKidz.cert.b5'), color: 'from-blue-400 to-cyan-500', icon: Bot },
    { label: t('aiStemKidz.cert.b6'), color: 'from-orange-400 to-red-500', icon: Trophy },
  ];
  const portfolios = t('aiStemKidz.cert.portfolio');
  return (
    <section className="py-20" data-testid="kidz-cert">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          <span className="gradient-text-gold">{t('aiStemKidz.cert.title')}</span>
        </h2>
        <p className="text-center text-[var(--text-secondary)] mb-12 max-w-2xl mx-auto">
          {t('aiStemKidz.cert.subtitle')}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-16">
          {badges.map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                        className="flex flex-col items-center text-center" data-testid={`badge-${i}`}>
              <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${b.color} flex items-center justify-center shadow-2xl mb-3 relative`}>
                <div className="absolute inset-1 rounded-full border-2 border-white/30" />
                <b.icon className="w-10 h-10 text-white relative z-10" />
              </div>
              <div className="text-xs font-bold text-[var(--accent-gold)] mb-1">{`LVL ${i + 1}`}</div>
              <div className="text-xs text-[var(--text-primary)] font-medium">{b.label}</div>
            </motion.div>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {portfolios.map((p, i) => (
            <div key={i} className="p-6 rounded-2xl glass border border-[var(--border-subtle)]" data-testid={`portfolio-${i}`}>
              <div className="aspect-video rounded-xl mb-4 flex items-center justify-center"
                   style={{ background: `linear-gradient(135deg, ${['#7C3AED','#F59E0B','#22D3EE'][i]}33, ${['#FFD700','#EC4899','#10B981'][i]}33)` }}>
                {[<Palette key="p" className="w-12 h-12 text-white opacity-80" />,
                  <Video key="v" className="w-12 h-12 text-white opacity-80" />,
                  <Code2 key="c" className="w-12 h-12 text-white opacity-80" />][i]}
              </div>
              <h4 className="font-bold text-[var(--text-primary)] mb-1">{p.title}</h4>
              <p className="text-sm text-[var(--text-secondary)]">{p.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-[var(--text-muted)] mt-10 italic">
          {t('aiStemKidz.cert.footnote')}
        </p>
      </div>
    </section>
  );
}

// ---------- Section: Pricing ----------
function Pricing({ t }) {
  return (
    <section className="py-20 bg-[var(--bg-paper)]" id="pricing" data-testid="kidz-pricing">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
          <span className="gradient-text-gold">{t('aiStemKidz.pricing.title')}</span>
        </h2>
        <p className="text-center text-[var(--text-secondary)] mb-3 max-w-2xl mx-auto">
          {t('aiStemKidz.pricing.subtitle')}
        </p>
        <div className="flex items-center justify-center gap-2 mb-12">
          <Calendar className="w-4 h-4 text-[var(--accent-gold)]" />
          <span className="text-sm text-[var(--accent-gold)] font-semibold">{t('aiStemKidz.pricing.starts')}</span>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Card 1: 6-month one-time */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      className="relative p-8 rounded-3xl bg-gradient-to-br from-[var(--accent-gold)]/15 to-[var(--primary-main)]/15 border-2 border-[var(--accent-gold)] card-hover"
                      data-testid="pricing-card-onetime">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[var(--accent-gold)] text-[var(--bg-default)] text-xs font-bold uppercase">
              {t('aiStemKidz.pricing.bestValue')}
            </div>
            <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">{t('aiStemKidz.pricing.p1Title')}</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold gradient-text-gold">$11,999</span>
              <span className="text-[var(--text-secondary)] ml-2">MXN</span>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('aiStemKidz.pricing.p1Note')}</p>
            </div>
            <ul className="space-y-3 mb-8">
              {t('aiStemKidz.pricing.p1Features').map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-primary)]">
                  <CheckCircle2 className="w-4 h-4 text-[var(--accent-gold)] mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <a href={WA_ENROL} target="_blank" rel="noopener noreferrer"
               data-testid="pricing-onetime-cta"
               className="btn-primary w-full text-center inline-block pulse-cta">
              {t('aiStemKidz.pricing.cta')}
            </a>
          </motion.div>
          {/* Card 2: Monthly */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                      className="p-8 rounded-3xl glass border border-[var(--border-highlight)] card-hover"
                      data-testid="pricing-card-monthly">
            <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">{t('aiStemKidz.pricing.p2Title')}</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold text-[var(--primary-light)]">$2,255</span>
              <span className="text-[var(--text-secondary)] ml-2">MXN / {t('aiStemKidz.pricing.month')}</span>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('aiStemKidz.pricing.p2Note')}</p>
            </div>
            <ul className="space-y-3 mb-8">
              {t('aiStemKidz.pricing.p2Features').map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-primary)]">
                  <CheckCircle2 className="w-4 h-4 text-[var(--primary-light)] mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <a href={WA_ENROL} target="_blank" rel="noopener noreferrer"
               data-testid="pricing-monthly-cta"
               className="btn-secondary w-full text-center inline-block">
              {t('aiStemKidz.pricing.cta')}
            </a>
          </motion.div>
        </div>
        {/* Founding bonus */}
        <div className="max-w-3xl mx-auto mt-10 p-6 rounded-2xl bg-gradient-to-r from-[var(--accent-gold)]/20 to-[var(--primary-main)]/20 border border-[var(--accent-gold)]">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <Trophy className="w-10 h-10 text-[var(--accent-gold)] flex-shrink-0" />
            <div className="flex-1">
              <h4 className="font-bold text-[var(--accent-gold)] mb-1">{t('aiStemKidz.pricing.bonusTitle')}</h4>
              <p className="text-sm text-[var(--text-primary)]">
                {t('aiStemKidz.pricing.bonusDesc')} {' '}
                <a href={HANDBOOK_URL} target="_blank" rel="noopener noreferrer"
                   className="underline text-[var(--accent-gold)] hover:text-[var(--accent-yellow)]"
                   data-testid="pricing-handbook-link">
                  "El poder del prompt perfecto"
                </a> {' '}
                <span className="text-[var(--text-muted)]">($3,000 MXN {t('aiStemKidz.pricing.value')})</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Target Audience ----------
function TargetAudience({ t }) {
  const audiences = [
    { icon: Lightbulb, label: t('aiStemKidz.audience.a1') },
    { icon: BookOpen, label: t('aiStemKidz.audience.a2') },
    { icon: GraduationCap, label: t('aiStemKidz.audience.a3') },
    { icon: Star, label: t('aiStemKidz.audience.a4') },
    { icon: ShieldCheck, label: t('aiStemKidz.audience.a5') },
  ];
  return (
    <section className="py-20" data-testid="kidz-audience">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          <span className="text-[var(--text-primary)]">{t('aiStemKidz.audience.title')}</span>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl mx-auto">
          {audiences.map((a, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                        className="p-6 rounded-2xl glass border border-[var(--border-subtle)] text-center hover:border-[var(--accent-gold)] transition-all"
                        data-testid={`audience-${i}`}>
              <a.icon className="w-8 h-8 text-[var(--accent-gold)] mx-auto mb-3" />
              <p className="text-sm text-[var(--text-primary)] font-medium">{a.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Social Proof ----------
function SocialProof({ t }) {
  const loops = t('aiStemKidz.social.loops');
  return (
    <section className="py-20 bg-[var(--bg-paper)]" data-testid="kidz-social">
      <div className="container-custom">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          <span className="gradient-text-gold">{t('aiStemKidz.social.title')}</span>
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {loops.map((l, i) => (
            <div key={i} className="p-6 rounded-2xl glass border border-[var(--border-subtle)]" data-testid={`loop-${i}`}>
              <Users className="w-8 h-8 text-[var(--primary-light)] mb-3" />
              <p className="text-sm text-[var(--text-primary)]">{l}</p>
            </div>
          ))}
        </div>
        <div className="max-w-2xl mx-auto p-8 rounded-3xl glass-strong border border-[var(--accent-gold)]/30 text-center">
          <div className="flex items-center justify-center gap-1 mb-4">
            {[1,2,3,4,5].map(s => <Star key={s} className="w-5 h-5 text-[var(--accent-gold)] fill-[var(--accent-gold)]" />)}
          </div>
          <p className="text-lg italic text-[var(--text-primary)] mb-4">
            "{t('aiStemKidz.social.testimonial')}"
          </p>
          <p className="text-sm text-[var(--text-muted)]">— {t('aiStemKidz.social.testimonialBy')}</p>
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Risk Mitigation (Accordion) ----------
function RiskMitigation({ t }) {
  const [open, setOpen] = useState(0);
  const risks = t('aiStemKidz.risk.items');
  return (
    <section className="py-20" data-testid="kidz-risk">
      <div className="container-custom max-w-3xl">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          <span className="text-[var(--text-primary)]">{t('aiStemKidz.risk.title')}</span>
        </h2>
        <div className="space-y-3">
          {risks.map((r, i) => (
            <div key={i} className="rounded-2xl glass border border-[var(--border-subtle)] overflow-hidden" data-testid={`risk-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                      data-testid={`risk-toggle-${i}`}>
                <span className="font-semibold text-[var(--text-primary)]">{r.q}</span>
                <ChevronDown className={`w-5 h-5 text-[var(--accent-gold)] transition-transform ${open === i ? 'rotate-180' : ''}`} />
              </button>
              {open === i && (
                <div className="px-6 pb-4 text-sm text-[var(--text-secondary)] border-t border-[var(--border-subtle)] pt-3">
                  {r.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Section: Closing CTA ----------
function ClosingCTA({ t }) {
  return (
    <section className="py-20 bg-[var(--bg-paper)] relative overflow-hidden" data-testid="kidz-closing">
      <div className="absolute inset-0 opacity-30"
           style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(124,58,237,0.25), transparent 50%), radial-gradient(circle at 70% 50%, rgba(255,215,0,0.2), transparent 50%)' }} />
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-5 gap-10 items-center max-w-6xl mx-auto">
          {/* Certificate image */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                      className="lg:col-span-2 relative">
            <div className="relative rounded-3xl overflow-hidden border border-[var(--accent-gold)]/40 aspect-square max-w-sm mx-auto shadow-2xl">
              <img
                src={IMG_CERTIFICATE}
                alt="Child proudly holding a graduation certificate"
                loading="lazy"
                className="w-full h-full object-cover"
                data-testid="img-certificate"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-default)]/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 p-3 rounded-xl glass-strong border border-[var(--accent-gold)]/30">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[var(--accent-gold)]" />
                  <span className="text-xs text-[var(--text-primary)] font-semibold">AI STEM Kidz · Master Creator</span>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-3 text-center lg:text-left">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-[var(--text-primary)]">{t('aiStemKidz.closing.title1')}</span>{' '}
              <span className="gradient-text-gold">{t('aiStemKidz.closing.title2')}</span>
            </h2>
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-8">
              <Calendar className="w-5 h-5 text-[var(--accent-gold)]" />
              <span className="text-[var(--accent-gold)] font-semibold">{t('aiStemKidz.pricing.starts')}</span>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4">
              <a href={WA_ENROL} target="_blank" rel="noopener noreferrer"
                 data-testid="closing-enrol-btn"
                 className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4 pulse-cta">
                <Rocket className="w-5 h-5" />
                {t('aiStemKidz.closing.enrol')}
              </a>
              <a href={CALENDLY} target="_blank" rel="noopener noreferrer"
                 data-testid="closing-book-btn"
                 className="btn-secondary inline-flex items-center gap-2 text-lg px-8 py-4">
                <MessageCircle className="w-5 h-5" />
                {t('aiStemKidz.closing.book')}
              </a>
            </div>
            <div className="mt-10 text-xs text-[var(--text-muted)] space-y-2">
              <p>{t('aiStemKidz.closing.disclaimer')}</p>
              <p className="text-[var(--text-secondary)] font-medium">{t('aiStemKidz.closing.founder')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Floating Sticky CTA ----------
function FloatingCTA({ t }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <a href={WA_ENROL} target="_blank" rel="noopener noreferrer"
       data-testid="floating-enrol-btn"
       className="fixed left-4 bottom-24 sm:bottom-20 z-40 btn-primary inline-flex items-center gap-2 shadow-2xl pulse-cta">
      <Rocket className="w-4 h-4" />
      <span className="hidden sm:inline">{t('aiStemKidz.floating')}</span>
      <span className="sm:hidden">{t('aiStemKidz.floatingShort')}</span>
    </a>
  );
}

// ---------- MAIN PAGE ----------
export default function AIStemKidzPage() {
  const { t, language } = useLanguage();
  // Spots counter — visual demo, starts at 25 and decrements slightly
  const [spots, setSpots] = useState(25);
  useEffect(() => {
    document.title = language === 'es'
      ? 'AI STEM Kidz – Alfabetización en IA para edades 8–12'
      : 'AI STEM Kidz – AI Literacy for Ages 8–12';
    // Random gentle "live" decrement
    const interval = setInterval(() => {
      setSpots(prev => (prev > 11 && Math.random() > 0.7 ? prev - 1 : prev));
    }, 18000);
    return () => clearInterval(interval);
  }, [language]);

  return (
    <div className="bg-[var(--bg-default)] pb-20" data-testid="ai-stem-kidz-page">
      {/* Animated grid bg */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.04] -z-10"
           style={{ backgroundImage: 'linear-gradient(rgba(255,215,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(124,58,237,0.5) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
      <Hero t={t} spots={spots} />
      <ProblemSolution t={t} />
      <ValueProp t={t} />
      <HowItWorks t={t} />
      <Curriculum t={t} />
      <ParentalCoPilot t={t} />
      <Certification t={t} />
      <Pricing t={t} />
      <TargetAudience t={t} />
      <SocialProof t={t} />
      <RiskMitigation t={t} />
      <ClosingCTA t={t} />
      <FloatingCTA t={t} />
    </div>
  );
}
